/** @jsxImportSource @emotion/react */
import { ReactNode, memo, MouseEvent } from 'react';

import Spinner, { ColorType } from '../Spinner';
import { TAP_HIGHLIGHT_TRANSPARENT } from '../../constants';

import * as styles from './Button.style';

export type ButtonColorType = 'primary' | 'secondary' | 'gray' | 'outline';
type ButtonSizeType = 'xlarge' | 'large' | 'medium' | 'small';
export const ButtonTypes: ButtonColorType[] = ['primary', 'secondary', 'gray', 'outline'];
export const ButtonSizes: ButtonSizeType[] = ['xlarge', 'large', 'medium', 'small'];

export interface ButtonProps {
  className?: string;
  children: ReactNode;
  size?: 'xlarge' | 'large' | 'medium' | 'small';
  type?: 'primary' | 'secondary' | 'gray' | 'outline';
  disabled?: boolean;
  loading?: boolean;
  full?: boolean;
  disableTapHighlight?: boolean;
  onClick?: (event?: MouseEvent) => void;
}

const typeColors: { [key in string]: ColorType } = {
  primary: 'White',
  secondary: 'Blue',
  gray: 'Gray',
  outline: 'Gray',
};

const spinnerSizes = {
  xlarge: 32,
  large: 24,
  medium: 20,
  small: 16,
};

function Button({
  className,
  children,
  size = 'large',
  type = 'primary',
  disabled = false,
  loading = false,
  full,
  disableTapHighlight = false,
  onClick,
}: ButtonProps) {
  function handleOnClick(event: MouseEvent) {
    if (disabled) {
      return;
    }

    onClick && onClick(event);
  }

  return (
    <span role="button" className={className} css={[styles.wrapper, full && styles.full]}>
      <button
        type="button"
        className={`mrt-button ${disableTapHighlight ? TAP_HIGHLIGHT_TRANSPARENT : ''}`}
        css={styles.container(size, type, full)}
        onClick={handleOnClick}
        disabled={disabled}
      >
        {loading && (
          <Spinner css={styles.loading} size={spinnerSizes[size]} color={typeColors[type]} />
        )}
        <span css={[styles.buttonContent, loading && styles.hiddenChildren]}>{children}</span>
      </button>
    </span>
  );
}

export default memo(Button);
